import type { VoidFunctionComponent } from 'react';
import type Stripe from 'stripe-latest';
import * as _ from 'lodash';
import type { FossaSubscriptionPlan } from '../../../../types';
import { isOrgInFreeTier } from '../../../../helpers/subscriptions';
import SubscriptionBannerTrialInProgress from './SubscriptionBannerTrialInProgress';
import SubscriptionBannerUpgrade from './SubscriptionBannerUpgrade';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import { useGetCurrentPlan } from '../../../../hooks/subscriptions/useGetCurrentPlan';
import SubscriptionBannerPending from './SubscriptionBannerPending';
import { getDynamicSubscriptionPlanName } from '../utils';

type SubscriptionBannerProps = {
  data: FossaSubscriptionPlan;
};
export const SubscriptionBanner: VoidFunctionComponent<SubscriptionBannerProps> = ({
  data,
}) => {
  if (isOrgInFreeTier()) {
    return (<SubscriptionBannerUpgrade hasUsedFreeTrial={data.hasUsedFreeTrial} />);
  }

  if (data.endDate && data.trialDaysRemaining) {
    return (
      <SubscriptionBannerTrialInProgress
        hasPaymentInfo={!(_.isEmpty(data.paymentInfo?.last4))}
        trialDaysRemaining={data.trialDaysRemaining}
        endDate={data.endDate}
      />);
  }

  if (data.endDate && data.nextScheduledPrice) {
    return (
      <SubscriptionBannerPending
        planName={getDynamicSubscriptionPlanName((data.nextScheduledPrice?.product as Stripe.Product))}
        startDate={data.endDate}
      />);
  }

  return null;
};

export const SubscriptionBannerContainer: VoidFunctionComponent = () => {
  const hasPermission = useHasPermission();
  const { data } = useGetCurrentPlan();
  const hasAccessToBilling = hasPermission({
    action: Action.Edit,
    resourceType: ResourceType.OrganizationBillingSettings,
  });

  // Don't render the Subscription banner if the user doesn't have access to the billing page
  if (!hasAccessToBilling) {
    return null;
  }

  if (!data) {
    return null;
  }

  return (<SubscriptionBanner data={data} />);
};
